import { Box, Grid, MenuItem, Select } from "@mui/material";
import * as React from "react";
import { SaveDataObj } from "source/framework/saveData";
import {
    AttachmentImageSize,
    ConfirmationDialogResult,
    EventLocationUseType,
    EventObserverActionUseType,
} from "ui/Scripts/source/models/enums";
import { IEventSetting } from "ui/Scripts/source/models/eventSettings/eventSetting";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "source/framework/appUtils";
import * as eventService from "source/models/services/eventService";
import { DialogSettingsCard } from "source/components/framework/Container/DialogSettingsCard";
import { DialogButtons } from "source/components/framework/Dialog/DialogButtons";
import { FormField } from "ui/Scripts/source/components/framework/FormField";
import { GridRow } from "source/components/framework/Grid/GridRow";

export interface IEventSettingsProp {
  classes?: string;
  companyId: string;
  onOk: (closeDialog: boolean) => void;
  onCancel: (closeDialog: boolean) => void;
}

export interface IEventSettingsState {
  attachmentImageSize: AttachmentImageSize;
  observerActionUseType: EventObserverActionUseType;
  locationUseType: EventLocationUseType;
  item: IEventSetting;
  rowId: string;
}

export class EventSettings extends React.Component<
  IEventSettingsProp,
  IEventSettingsState
> {
    private orgStateHash: string = "";

    constructor(props: IEventSettingsProp) {
        super(props);
        this.state = {
            attachmentImageSize: AttachmentImageSize.Large,
            observerActionUseType: EventObserverActionUseType.NotInUse,
            locationUseType: EventLocationUseType.NotInUse,
            item: null,
            rowId: ""
        };
    }

    getSaveDataFromState = (
        props: IEventSettingsProp,
        state: IEventSettingsState
    ): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("id", state.item?.id);
        result.append("companyId", !props.companyId ? null : props.companyId);
        result.append("rowId", state.rowId);
        result.append("attachmentImageSize", state.attachmentImageSize);
        result.append("observerActionUseType", state.observerActionUseType);
        result.append("locationUseType", state.locationUseType);
        return result;
    };

    loadEventSettings = async () => {
        const props = this.props;
        const result = await AppUtils.callService(
            () => eventService.getEventSetting(props.companyId),
            false
        );
        if (!result) return;
        const state = {
            ...this.state,
            item: result,
            rowId: result.rowId,
            attachmentImageSize: result.attachmentImageSize,
            observerActionUseType: result.observerActionUseType,
            locationUseType: result.locationUseType,
        };
        this.orgStateHash = this.getSaveDataFromState(this.props, state).hash;
        this.setState(state);
    };

    componentDidMount(): void {
        this.loadEventSettings();
    }

    handleChange = (event) => {
        const target = event.target;
        const name: string = target.name;
        const value: string = target.value;
        if (name === "attachmentImageSize") {
            this.setState({ attachmentImageSize: target.value });
        } else if (name === "observerActionUseType") {
            this.setState({ observerActionUseType: value.toInteger() });
        } else if (name === "locationUseType") {
            this.setState({ locationUseType: value.toInteger() });
        }
    };

    saveToDb = async (): Promise<boolean> => {
        const props = this.props;
        const state = this.state;
        const saveData = this.getSaveDataFromState(props, state);
        //Call server
        const result = await AppUtils.callService(() =>
            eventService.saveEventSetting(saveData.obj)
        );
        if (!result) return null;
        const newState = {
            ...this.state,
            rowId: result.rowId
        };
        this.orgStateHash = this.getSaveDataFromState(props, newState).hash;
        this.setState(newState);
        return true;
    };

    handleSave = async (closeDialog: boolean) => {
        if (!(await this.saveToDb())) return;
        this.props.onOk(closeDialog);
    };

    handleCancel = async (closeDialog: boolean) => {
        const props = this.props;
        const state = this.state;
        const cancelResult = await AppUtils.cancel(this.getSaveDataFromState(props, state).hash, this.orgStateHash, null);
        if (cancelResult === ConfirmationDialogResult.Cancel) return false;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            this.handleSave(closeDialog);
        } else {
            props.onCancel(closeDialog);
        }
        return true;
    };

    render() {
        const props = this.props;
        const state = this.state;
        if (!state.item) return null;
        return (
            <Box display="flex" flexDirection="column" flexGrow={2} justifyContent="space-between">
                <DialogSettingsCard
                    fitToContainerHeight
                    title={props.companyId ? Translations.EventSettings : Translations.DefaultEventSettings}
                    info={props.companyId ? Translations.EventModuleInfoSettings : undefined}
                    cardContent={
                        <>
                            <GridRow>
                                <Grid item sm={12} md={6}>
                                    <FormField title={Translations.AttachmentImageSize}>
                                        <Select
                                            name="attachmentImageSize"
                                            title={Translations.AttachmentImageSize}
                                            value={state.attachmentImageSize}
                                            onChange={this.handleChange}
                                        >
                                            <MenuItem value={AttachmentImageSize.Normal}>
                                                {Translations.NormalSize}
                                            </MenuItem>
                                            <MenuItem value={AttachmentImageSize.Large}>
                                                {Translations.LargeSize}
                                            </MenuItem>
                                        </Select>
                                    </FormField>
                                </Grid>
                                <Grid item sm={12} md={6}>
                                    <FormField title={Translations.ObserverAction}>
                                        <Select
                                            name="observerActionUseType"
                                            title={Translations.ObserverAction}
                                            value={state.observerActionUseType.toString(10)}
                                            onChange={this.handleChange}
                                        >
                                            <MenuItem value={EventObserverActionUseType.NotInUse.toString(10)}>
                                                {Translations.NotInUse}
                                            </MenuItem>
                                            <MenuItem value={EventObserverActionUseType.InUse.toString(10)}>
                                                {Translations.InUse}
                                            </MenuItem>
                                            <MenuItem value={EventObserverActionUseType.Required.toString(10)}>
                                                {Translations.Required}
                                            </MenuItem>
                                        </Select>
                                    </FormField>
                                </Grid>
                            </GridRow>
                            <GridRow>
                                <Grid item sm={12} md={6}>
                                    <FormField title={Translations.Location}>
                                        <Select
                                            name="locationUseType"
                                            title={Translations.Location}
                                            value={state.locationUseType.toString(10)}
                                            onChange={this.handleChange}
                                        >
                                            <MenuItem
                                                value={EventLocationUseType.NotInUse.toString(10)}
                                            >
                                                {Translations.NotInUse}
                                            </MenuItem>
                                            <MenuItem value={EventLocationUseType.InUse.toString(10)}>
                                                {Translations.InUse}
                                            </MenuItem>
                                        </Select>
                                    </FormField>
                                </Grid>
                            </GridRow>
                        </>
                    }
                />
                <Box display="flex" justifyContent="flex-end" paddingTop="1.9rem" sx={{ width: "100%" }}>
                    <DialogButtons
                        onCancel={() => this.handleCancel(true)}
                        onSave={() => this.handleSave(false)}
                        saveDisabled={this.getSaveDataFromState(props, this.state).hash === this.orgStateHash}
                    />
                </Box>
            </Box>
        );
    }
}
